import { useChat } from '@ai-sdk/react';
import { jsonSchema, tool, type FileUIPart, ToolSet } from 'ai';
import classnames from 'classnames';
import { config } from 'lib-shared-backend-config';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import {
  type SidekickTask,
  useReportSidekickActivity,
  useSidekickTask,
} from 'utils/sidekick';
import { useSidekickStarters } from 'utils/sidekickStarters';
import { useKonamiCode } from 'utils/useKonamiCode';

import { usePanelGeometry } from '../../hooks/usePanelGeometry';
import { useSidekickMessages } from '../../hooks/useSidekickMessages';
import { useSidekickPersistence } from '../../hooks/useSidekickPersistence';
import {
  createSubagentMcpClient,
  type OnMutation,
  type Subagent,
  type SubagentMcpClient,
} from '../../utils/sdk/mcpClient';
import {
  type SidekickAgentMode,
  type MimUIMessage,
} from '../../utils/sdk/structuredResponse';
import { createSubagentTool } from '../../utils/sdk/subagents';
import { createBrowserChatTransport } from '../../utils/sdk/transport';
import { toSidekickActivity } from '../../utils/sidekickActivity';
import { NAMESPACE_INVALIDATIONS } from '../../utils/sidekickTags';

import { SidekickComposer } from './components/sidekickComposer/sidekickComposer';
import { SidekickHeader } from './components/sidekickHeader/sidekickHeader';
import { SidekickMessageList } from './components/sidekickMessageList/sidekickMessageList';
import { SidekickResizeHandles } from './components/sidekickResizeHandles/sidekickResizeHandles';
import {
  SidekickChatProvider,
  type SidekickChat,
} from './sidekickChat.context';
import styles from './sidekickWidget.module.scss';

const DevPage = lazy(() => import('../devPage/devPage'));

export type SidekickMode = 'floating' | 'docked';

/** A prompt waiting to be sent, identified so the same text can be asked twice. */
export type PendingPrompt = {
  id: string;
  text: string;
  /** Switches to this chat before sending. */
  chatId?: string;
  /** Marks a page event, and is what the user sees of it in the chat. */
  pageEventSummary?: string;
};

type SidekickProps = {
  className: string;
  isMinimized: boolean;
  pendingPrompt: PendingPrompt | null;
  onPromptSent: () => void;
  onMinimize: () => void;
  onUnread: () => void;
};

type SubagentConfig = {
  mainPrompt: string;
  subagents: Subagent[];
  hasError: boolean;
  isLoaded: boolean;
};

const DEFAULT_MAIN_PROMPT = 'chat-main-agent';

const AGENT_MODE_CONTEXT = {
  do: 'The current mode is do mode.',
  plan: 'The current mode is plan mode.',
} as const satisfies Record<SidekickAgentMode, string>;

const createChatId = (): string => uuid();

const markInterrupted = (message: MimUIMessage): MimUIMessage => ({
  ...message,
  parts: [...message.parts, { type: 'data-interrupted', data: true }],
});

const createPageTools = (task: SidekickTask | null): ToolSet =>
  Object.fromEntries(
    Object.entries(task?.tools ?? {}).map(([name, pageTool]) => [
      name,
      tool({
        description: pageTool.description,
        inputSchema: jsonSchema<never>(pageTool.inputSchema),
        metadata: { title: pageTool.title },
        execute: async (input) => {
          try {
            return await pageTool.execute(input as never);
          } catch (error) {
            console.error('[sidekick]', error);
            return `The page could not run ${name} with that input. Check it against the tool's input schema and try again.`;
          }
        },
      }),
    ]),
  );

const withTaskBrief = (context: string, task: SidekickTask | null): string =>
  task
    ? [
        context,
        typeof task.brief === 'function' ? task.brief() : task.brief,
        `Your subagents can call these page tools: ${Object.keys(task.tools).join(', ')}. You cannot call them yourself; ask a subagent to call them and give it the values to use.`,
        'Messages that start with "[Page event]" are sent by the page, not typed by the user. Continue the task from what they report.',
      ].join('\n')
    : context;

const EMPTY_SUBAGENT_CONFIG: SubagentConfig = {
  mainPrompt: DEFAULT_MAIN_PROMPT,
  subagents: [],
  hasError: false,
  isLoaded: false,
};

/**
 * Sidekick with browser-side agent loop. The main agent orchestrates the
 * loop and delegates server-side tool calls to subagents. See
 * https://ai-sdk.dev/docs/agents/subagents#specialized-orchestration for
 * details.
 */
export const SidekickWidget = ({
  className,
  isMinimized,
  pendingPrompt,
  onPromptSent,
  onMinimize,
  onUnread,
}: SidekickProps): JSX.Element => {
  const [chatId, setChatId] = useState(createChatId);

  const [subagentConfig, setSubagentConfig] = useState<SubagentConfig>(
    EMPTY_SUBAGENT_CONFIG,
  );

  const [mode, setMode] = useState<SidekickMode>('docked');

  const [agentMode, setAgentMode] = useState<SidekickAgentMode>('do');

  const agentModeRef = useRef(agentMode);
  agentModeRef.current = agentMode;

  const dispatch = useAppDispatch();

  const invalidateRef = useRef<OnMutation>(() => {});
  invalidateRef.current = (namespace) => {
    if (!namespace) return;

    const invalidate = NAMESPACE_INVALIDATIONS[namespace];
    if (!invalidate) return;

    dispatch(invalidate());
  };

  useEffect(() => {
    let clients: SubagentMcpClient | null = null;
    let isCancelled = false;

    void (async () => {
      try {
        const next = await createSubagentMcpClient({
          restUrl: config.url.rest,
          onMutation: (namespace) => invalidateRef.current(namespace),
          getAgentMode: () => agentModeRef.current,
        });
        if (isCancelled) {
          await next.dispose();
          return;
        }
        clients = next;
        setSubagentConfig({
          mainPrompt: next.mainPrompt,
          subagents: next.subagents,
          hasError: next.hasError,
          isLoaded: true,
        });
      } catch {
        // Marked as loaded so a pending prompt is still sent, without tools.
        if (!isCancelled) {
          setSubagentConfig({
            ...EMPTY_SUBAGENT_CONFIG,
            hasError: true,
            isLoaded: true,
          });
        }
      }
    })();

    return () => {
      isCancelled = true;
      if (clients) void clients.dispose();
    };
  }, []);

  const conversationId = useAppSelector(
    (state) => state.conversation.metaConversation?.conversation.id,
  );

  const subagentsRef = useRef(subagentConfig.subagents);
  subagentsRef.current = subagentConfig.subagents;

  const mainPromptRef = useRef(subagentConfig.mainPrompt);
  mainPromptRef.current = subagentConfig.mainPrompt;

  const starters = useSidekickStarters();
  const { pathname } = useLocation();

  const task = useSidekickTask();
  const taskRef = useRef(task);
  taskRef.current = task;

  // Search params are left out to keep filter state out of the prompt.
  const contextRef = useRef('');
  contextRef.current = [
    `The user is on the page ${pathname}. Use it as context when relevant to the question.`,
    conversationId
      ? `The agent is currently viewing the Dixa conversation with id ${conversationId}. Use it as context when relevant to the question.`
      : '',
  ]
    .filter(Boolean)
    .join('\n');

  const { persistTurn, markExisting } = useSidekickPersistence();

  const persistTurnRef = useRef(persistTurn);
  persistTurnRef.current = persistTurn;

  const transport = useMemo(
    () =>
      createBrowserChatTransport({
        buildTools: (writer, history) => {
          const page = {
            tools: createPageTools(taskRef.current),
            brief: taskRef.current?.subagentBrief,
          };

          return Object.fromEntries(
            subagentsRef.current.map((subagent) => [
              subagent.name,
              createSubagentTool(subagent, chatId, writer, history, page),
            ]),
          );
        },
        getMainPrompt: () => mainPromptRef.current,
        /** The mode is read here, not in render, so a switch made just before sending applies. Also send the mode on every turn so that the agent is aware of which mode it is in to feed back to the prompt. */
        getChatContext: () =>
          withTaskBrief(
            `${contextRef.current}\n${AGENT_MODE_CONTEXT[agentModeRef.current]}`,
            taskRef.current,
          ),
        onResponseStart: (turnChatId, prompt) => {
          void persistTurnRef.current({
            chatId: turnChatId,
            messages: [prompt],
          });
        },
      }),
    [chatId],
  );

  // onFinish always runs the newest closure, so the owner is pinned at send time.
  const streamChatIdRef = useRef(chatId);

  const { messages, sendMessage, regenerate, setMessages, status, stop } =
    useChat<MimUIMessage>({
      id: chatId,
      transport,
      experimental_throttle: 50,
      onFinish: ({
        message,
        messages: turn,
        isAbort,
        isDisconnect,
        isError,
      }) => {
        if (isMinimized) onUnread();

        const prompt = turn.findLast((candidate) => candidate.role === 'user');
        const hasReply = message.parts.length > 0;
        // A failed send with no reply is back in the composer as a draft.
        if (isError && !hasReply) return;

        const isInterrupted = hasReply && (isAbort || isDisconnect || isError);
        // Kept after an abort or error too, so a reload still shows what arrived.
        const reply = isInterrupted ? markInterrupted(message) : message;
        if (isInterrupted) {
          setMessages((prev) =>
            prev.map((candidate) =>
              candidate.id === reply.id ? reply : candidate,
            ),
          );
        }

        void persistTurn({
          chatId: streamChatIdRef.current,
          messages: [prompt, hasReply ? reply : undefined].filter(
            (candidate) => candidate !== undefined,
          ),
        });
      },
      // A failed send is not shown as sent; the composer keeps it as a draft.
      onError: () =>
        setMessages((prev) =>
          prev.at(-1)?.role === 'user' ? prev.slice(0, -1) : prev,
        ),
    });

  const [isDevModeAvailable, setIsDevModeAvailable] = useState(!PRODUCTION);
  const [isDevMode, setIsDevMode] = useState(false);

  useKonamiCode(() => setIsDevModeAvailable(true));
  const { loadChat, loadOlder, isLoadingOlder } =
    useSidekickMessages(setMessages);

  const send = useCallback(
    (text: string, files?: FileUIPart[]) => {
      streamChatIdRef.current = chatId;
      void sendMessage({ text, files });
    },
    [chatId, sendMessage],
  );

  const handleRetry = useCallback(() => {
    streamChatIdRef.current = chatId;
    void regenerate();
  }, [chatId, regenerate]);

  const handleAgentModeChange = useCallback(
    (nextAgentMode: SidekickAgentMode) => {
      setAgentMode(nextAgentMode);
      // A message can be sent before the next render, so the context must already see the new mode.
      agentModeRef.current = nextAgentMode;
    },
    [],
  );

  const handleStartPlan = useCallback(() => {
    handleAgentModeChange('do');
    send('Carry out the plan.');
  }, [handleAgentModeChange, send]);

  useReportSidekickActivity(
    toSidekickActivity({
      status,
      messages,
      isMinimized,
      hasPendingPrompt: pendingPrompt !== null,
    }),
  );

  const { panelRef, panelStyle, startDrag, startResize, isInteracting } =
    usePanelGeometry(mode === 'floating');

  const sentPromptIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pendingPrompt || !subagentConfig.isLoaded) return;
    // 'error' is terminal, so waiting for 'ready' would hold the prompt forever.
    if (status === 'submitted' || status === 'streaming') return;
    if (sentPromptIdRef.current === pendingPrompt.id) return;
    // The prompt is sent once this chat is in place.
    if (pendingPrompt.chatId && pendingPrompt.chatId !== chatId) {
      setChatId(pendingPrompt.chatId);
      return;
    }
    sentPromptIdRef.current = pendingPrompt.id;
    if (pendingPrompt.pageEventSummary) {
      streamChatIdRef.current = chatId;
      void sendMessage({
        role: 'user',
        parts: [
          { type: 'text', text: `[Page event] ${pendingPrompt.text}` },
          { type: 'data-page-event', data: pendingPrompt.pageEventSummary },
        ],
      });
    } else {
      send(pendingPrompt.text);
    }
    onPromptSent();
  }, [
    pendingPrompt,
    subagentConfig.isLoaded,
    status,
    send,
    onPromptSent,
    chatId,
    sendMessage,
  ]);

  const chat = useMemo<SidekickChat>(
    () => ({
      chatId,
      startNewChat: () => setChatId(createChatId()),
      selectChat: (chatId) => {
        setChatId(chatId);
        markExisting(chatId);
        loadChat(chatId);
      },
    }),
    [chatId, loadChat, markExisting],
  );

  const isDocked = mode === 'docked';

  return (
    <SidekickChatProvider value={chat}>
      <aside
        ref={panelRef}
        hidden={isMinimized}
        className={classnames(
          className,
          isDocked ? styles.docked : styles.drawer,
          { [styles.interacting]: isInteracting },
        )}
        style={isDocked ? undefined : panelStyle}
      >
        <div className={styles.body}>
          <SidekickHeader
            onMinimize={onMinimize}
            mode={mode}
            onToggleMode={() =>
              setMode((prev) => (prev === 'floating' ? 'docked' : 'floating'))
            }
            isDevModeAvailable={isDevModeAvailable}
            isDevMode={isDevMode}
            onToggleDevMode={() => setIsDevMode((prev) => !prev)}
            dragHandleProps={
              isDocked
                ? undefined
                : {
                    onPointerDown: startDrag,
                    className: styles.dragHandle,
                  }
            }
          />
          {isDevMode ? (
            <Suspense fallback={null}>
              <DevPage />
            </Suspense>
          ) : (
            <>
              <SidekickMessageList
                messages={messages}
                starters={starters}
                isLoadingOlder={isLoadingOlder}
                status={status}
                onSelectChoice={send}
                onRetry={handleRetry}
                onStartPlan={handleStartPlan}
                onReachTop={loadOlder}
              />
              <SidekickComposer
                status={status}
                agentMode={agentMode}
                onAgentModeChange={handleAgentModeChange}
                onSend={send}
                onStop={stop}
              />
            </>
          )}
        </div>
        {!isDocked && <SidekickResizeHandles onResizeStart={startResize} />}
      </aside>
    </SidekickChatProvider>
  );
};
